import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { debounce } from "lodash";
import Actions from "../../../components/Common/Actions";
import AntdBreadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import ListPageLayout from "../../../components/Layout";

const AddNewServiceProvider = () => {
  const navigate = useNavigate();
  const addNewServiceProvider = {
    id: "addnewpost",
    onClick: () => navigate("/home-healthcare-service-provider-form"),
    style: { width: "240px" },
    label: "Add New Service Provider",
  };
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortName, setSortName] = useState("Newest First");

  // Full Data
  const fullData = [
    {
      sno: "1",
      name: "Julie R",
      spl1: "Nursing Support Service",
      spl2: "Mom & Baby care",
      date: "01-02-2022",
      gender: "Female",
      age: "34",
      dob: "02-08-1997",
      fee: "250",
      mobile: "+9192226 25629",
      email: "julie@gmail.com",
      city: "Bangalore",
      pincode: "421 260",
      license: "KA2411641",
      exp: "2",
      edu: "Bsc Nursing",
      hos: "Advaith Homecare, bangalore",
      total: "1",
      language: "Kanada",
      associate: "Karnataka Nursing Counsil",
      status: "Active",
    },
    {
      sno: "1",
      name: "Ahmed K",
      spl1: "Physiotheraphy",
      spl2: "-",
      date: "30-01-2022",
      gender: "male",
      age: "32",
      dob: "09-08-1987",
      fee: "250",
      mobile: "+91 92226 25629",
      email: "ahmed@gmail.com",
      city: "Jaipur",
      pincode: "626023",
      license: "RJ642611",
      exp: "14",
      edu: "Diplomo in Physiotherapy",
      hos: "-",
      total: "4",
      language: "Hindi",
      associate: "Rajasthan Physiotheropist Forum",
      status: "Track",
    },
  ];

  // SEARCH + SORT + PAGINATION
  const filteredSortedData = useMemo(() => {
    let temp = [...fullData];

    // Search
    if (search) {
      const lowerSearch = search.toLowerCase();
      temp = temp.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(lowerSearch),
        ),
      );
    }

    // Sort
    switch (sort) {
      case "newest":
        temp.sort((a, b) => b.sno.localeCompare(a.sno));
        break;
      case "oldest":
        temp.sort((a, b) => a.sno.localeCompare(b.sno));
        break;
      case "ascending":
        temp.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "descending":
        temp.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    return temp;
  }, [search, sort]);

  const totalItems = filteredSortedData.length;
  const totalPages = Math.ceil(totalItems / limit);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * limit;
    const end = start + limit;
    return filteredSortedData.slice(start, end);
  }, [filteredSortedData, currentPage, limit]);

  const handleSortClick = (selectedSort) => {
    setSort(selectedSort);
    setSortName(
      {
        newest: "Newest First",
        oldest: "Oldest First",
        ascending: "Alphabetic A-Z",
        descending: "Alphabetic Z-A",
      }[selectedSort] || "Sort by",
    );
  };

  const debouncedSearch = debounce((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, 500);

  const onChangeSearch = (value) => debouncedSearch(value);

  const handlePageChange = (page) => setCurrentPage(page);

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      fixed: "left",
      render: (text, record, index) => Number(0) + (index + 1),
    },
    {
      title: "Name of Doctor",
      dataIndex: "name",
      key: "name",
      fixed: "left",
    },
    {
      title: "Specialisation (Primary)",
      dataIndex: "spl1",
      key: "spl1",
    },
    {
      title: "Specialisation (Secondary)",
      dataIndex: "spl2",
      key: "spl2",
    },
    {
      title: "Date of On-boarding",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: "Age",
      dataIndex: "age",
      key: "age",
    },
    {
      title: "DOB",
      dataIndex: "dob",
      key: "dob",
    },
    {
      title: "Fees (INR) Per Hour",
      dataIndex: "fee",
      key: "fee",
    },
    {
      title: "Registered Mobile Number",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "Registered Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "City / Town",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
    },
    {
      title: "License / Registration Number",
      dataIndex: "license",
      key: "license",
    },
    {
      title: "Experience in (Yrs)",
      dataIndex: "exp",
      key: "exp",
    },
    {
      title: "Education Qualification",
      dataIndex: "edu",
      key: "edu",
    },
    {
      title: "Name of Hospital / Clinic Employed at or Owned",
      dataIndex: "hos",
      key: "hos",
    },
    {
      title: "Total Bookings Completed",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "Language Known",
      dataIndex: "language",
      key: "language",
    },
    {
      title: "Associated Councils",
      dataIndex: "associate",
      key: "associate",
    },
    {
      title: "Actions",
      key: "actions",
      width: 350,
      render: (_, record) => (
        <Actions
          isActive={true}
          isEdit={true}
          isView={true}
          isDelete={true}
          onClickActive={() => {
            if (record.status === "Active") {
              navigate("/healthcare-activate");
            }
            if (record.status === "Track") {
              navigate("/healthcare-track");
            }
          }}
          recordActiveStatus={record.status}
          onClickView={() => navigate(`/healthcare-view`)}
          onClickEdit={() => navigate(`/home-healthcare-service-provider-edit`)}
        />
      ),
    },
  ];

  const breadcrumbsList = [
    {
      text: "Home Healthcare Service Providers",
      link: "/home-healthcare-service-provider",
    },
    { text: "List of Home Healthcare Service Providers", link: "" },
  ];

  return (
    <ListPageLayout
      breadcrumbs={
        <AntdBreadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewServiceProvider} />}
      totalCount={totalItems}
      pagination={{
        currentPage,
        skip: (currentPage - 1) * limit,
        limit,
        totalPages,
        onPageChange: handlePageChange,
      }}
      filters={
        <DoctorsFilterOptions
          limit={limit}
          setLimit={setLimit}
          search={search}
          onChange={onChangeSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource: paginatedData,
        className: "mx-3 pt-2 table-scrollable medicineList_table",
        pagination: false,
      }}
    />
  );
};

export default AddNewServiceProvider;
